import {
  Snackbar,
  Alert,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  MenuItem,
  Stack,
} from "@mui/material";

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";
import usePageTitle from "app/pageTitle";

import MDInput from "theme/components/MDInput";
import MDButton from "theme/components/MDButton";
import axios from "axios.js";
import { useAwaitDialog } from "app/components/status_dialog";

function TerritoryPage() {
  const pageTitle = "Territory";
  usePageTitle(pageTitle);

  const navigate = useNavigate();
  const { ref } = useParams();

  const statusDialog = useAwaitDialog();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");

  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [refreshPage, setRefreshPage] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);

  const [territory, setTerritory] = useState({});
  const [boundaries, setBoundaries] = useState([]);
  const [territoryBoundaries, setTerritoryBoundaries] = useState([]);

  const [openAddDialog, setOpenAddDialog] = useState(false);

  const [boundaryType, setBoundaryType] = useState("custom");

  const [selectedCustomBoundaries, setSelectedCustomBoundaries] = useState([]);

  const [selectedAdm0, setSelectedAdm0] = useState("");
  const [selectedAdm1, setSelectedAdm1] = useState([]);
  const [selectedAdm2, setSelectedAdm2] = useState([]);

  const [search, setSearch] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);

      await axios
        .get(`/portal/territories/territories/${ref}/fetch.json`)
        .then((result) => {
          setShowLoading(false);

          if (result.data.status !== "success") {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);

            return;
          }

          const territoryData = result.data.data.territory;

          setTerritory(territoryData);

          setBoundaries(territoryData.boundaries || []);

          setTerritoryBoundaries(territoryData.territory_boundaries || []);

          setPageLoaded(true);
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");

          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );

          setOpen(true);
        });
    };

    fetchData();
  }, [ref, refreshPage]);

  const assignedBoundaryReferences = territoryBoundaries.map((item) => item.boundary);

  const availableBoundaries = boundaries.filter(
    (item) => !assignedBoundaryReferences.includes(item.reference)
  );

  const customBoundaries = availableBoundaries.filter((item) => item.system === false);

  const systemBoundaries = availableBoundaries.filter((item) => item.system === true);

  const adm0Boundaries = systemBoundaries.filter((item) => item.type === "ADM0");

  const adm1Boundaries = systemBoundaries.filter(
    (item) => item.type === "ADM1" && item.parent === selectedAdm0
  );

  const adm2Boundaries = systemBoundaries.filter(
    (item) => item.type === "ADM2" && selectedAdm1.includes(item.parent)
  );

  const filteredCustomBoundaries = customBoundaries.filter((item) =>
    item.title.toLowerCase().includes(search.toLowerCase())
  );

  function resetBoundarySelection() {
    setSelectedCustomBoundaries([]);

    setSelectedAdm0("");
    setSelectedAdm1([]);
    setSelectedAdm2([]);

    setSearch("");
  }

  function handleBoundaryTypeChange(type) {
    setBoundaryType(type);

    setSelectedCustomBoundaries([]);

    setSelectedAdm0("");
    setSelectedAdm1([]);
    setSelectedAdm2([]);

    setSearch("");
  }

  function handleAdm0Change(value) {
    setSelectedAdm0(value);

    setSelectedAdm1([]);
    setSelectedAdm2([]);
  }

  function handleAdm1Change(value) {
    setSelectedAdm1(value);

    setSelectedAdm2([]);
  }

  function handleAdm2Change(value) {
    setSelectedAdm2(value);
  }

  function getSystemBoundariesToAdd() {
    if (selectedAdm2.length > 0) {
      return selectedAdm2;
    }

    if (selectedAdm1.length > 0) {
      return selectedAdm1;
    }

    if (selectedAdm0) {
      return [selectedAdm0];
    }

    return [];
  }

  async function addBoundaries() {
    let selectedBoundaries = [];

    if (boundaryType === "custom") {
      selectedBoundaries = selectedCustomBoundaries;
    } else {
      selectedBoundaries = getSystemBoundariesToAdd();
    }

    if (selectedBoundaries.length === 0) {
      setSeverity("error");
      setErrMsg("Select at least one boundary.");
      setOpen(true);

      return;
    }

    setOpenAddDialog(false);
    setShowLoading(true);

    try {
      const result = await axios.post("portal/territories/territories/boundaries/create.json", {
        reference: ref,
        boundaries: selectedBoundaries,
      });

      setShowLoading(false);

      if (result.data.status !== "success") {
        setSeverity("error");
        setErrMsg(result.data.data.message);
        setOpen(true);

        return;
      }

      resetBoundarySelection();

      await statusDialog.show("Boundaries added successfully.", "success", "Success!");

      setRefreshPage(!refreshPage);
    } catch (error) {
      setShowLoading(false);
      setSeverity("error");

      setErrMsg(
        `Unable to complete request. ${
          navigator.onLine ? "" : "Make sure you have an active internet connection"
        }`
      );

      setOpen(true);
    }
  }

  async function removeBoundary(reference) {
    if (!window.confirm("Remove this boundary from the territory?")) {
      return;
    }

    setShowLoading(true);

    await axios
      .get(`/portal/territories/territories/boundaries/${reference}/delete.json`)
      .then((result) => {
        setShowLoading(false);

        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");

        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );

        setOpen(true);
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={showLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Alert
          onClose={handleClose}
          severity={severity}
          sx={{
            width: "100%",
          }}
        >
          {errorMsg}
        </Alert>
      </Snackbar>

      <Dialog open={openAddDialog} fullWidth maxWidth="sm">
        <DialogTitle>Add Boundaries</DialogTitle>

        <DialogContent>
          <MDBox display="flex" gap={1} mb={3}>
            <MDButton
              size="small"
              variant={boundaryType === "custom" ? "contained" : "outlined"}
              color="dark"
              onClick={() => handleBoundaryTypeChange("custom")}
            >
              My Boundaries
            </MDButton>

            <MDButton
              size="small"
              variant={boundaryType === "system" ? "contained" : "outlined"}
              color="dark"
              onClick={() => handleBoundaryTypeChange("system")}
            >
              System Boundaries
            </MDButton>
          </MDBox>

          {boundaryType === "custom" && (
            <MDBox>
              <MDInput
                select
                fullWidth
                label="My Boundaries"
                value={selectedCustomBoundaries}
                SelectProps={{
                  style: {
                    padding: "12px 0px",
                  },
                  multiple: true,
                  renderValue: (selected) => (
                    <MDBox display="flex" flexWrap="wrap" gap={0.5}>
                      {selected.map((reference) => {
                        const boundary = customBoundaries.find(
                          (item) => item.reference === reference
                        );

                        return (
                          <Chip
                            key={reference}
                            size="small"
                            label={boundary ? boundary.title : reference}
                          />
                        );
                      })}
                    </MDBox>
                  ),
                }}
                onChange={(event) => setSelectedCustomBoundaries(event.target.value)}
              >
                {filteredCustomBoundaries.map((boundary) => (
                  <MenuItem key={boundary.reference} value={boundary.reference}>
                    <Checkbox checked={selectedCustomBoundaries.includes(boundary.reference)} />

                    <MDTypography variant="body2">{boundary.title}</MDTypography>
                  </MenuItem>
                ))}
              </MDInput>

              {customBoundaries.length === 0 && (
                <MDTypography display="block" variant="caption" color="text" mt={1}>
                  You have not created any custom boundaries yet.
                </MDTypography>
              )}
            </MDBox>
          )}

          {boundaryType === "system" && (
            <MDBox>
              <MDTypography variant="button" fontWeight="medium">
                System Boundaries
              </MDTypography>

              <MDTypography display="block" variant="caption" color="text" mb={2}>
                Select a country, state or local government area.
              </MDTypography>

              <MDInput
                select
                fullWidth
                label="Country"
                value={selectedAdm0}
                onChange={(event) => handleAdm0Change(event.target.value)}
                sx={{
                  mb: 3,
                }}
                SelectProps={{
                  style: {
                    padding: "12px 0px",
                  },
                }}
              >
                {adm0Boundaries.map((boundary) => (
                  <MenuItem key={boundary.reference} value={boundary.reference}>
                    {boundary.title}
                  </MenuItem>
                ))}
              </MDInput>

              {selectedAdm0 && (
                <MDInput
                  select
                  fullWidth
                  label="State / Province"
                  value={selectedAdm1}
                  onChange={(event) => handleAdm1Change(event.target.value)}
                  sx={{
                    mb: 3,
                  }}
                  SelectProps={{
                    multiple: true,
                    style: {
                      padding: "12px 0px",
                    },
                    renderValue: (selected) => (
                      <MDBox display="flex" flexWrap="wrap" gap={0.5}>
                        {selected.map((reference) => {
                          const boundary = adm1Boundaries.find(
                            (item) => item.reference === reference
                          );

                          return (
                            <Chip
                              key={reference}
                              size="small"
                              label={boundary ? boundary.title : reference}
                            />
                          );
                        })}
                      </MDBox>
                    ),
                  }}
                >
                  {adm1Boundaries.map((boundary) => (
                    <MenuItem key={boundary.reference} value={boundary.reference}>
                      <Checkbox checked={selectedAdm1.includes(boundary.reference)} />

                      <MDTypography variant="body2">{boundary.title}</MDTypography>
                    </MenuItem>
                  ))}
                </MDInput>
              )}

              {selectedAdm1.length > 0 && (
                <MDInput
                  select
                  fullWidth
                  label="LGA / District"
                  value={selectedAdm2}
                  onChange={(event) => handleAdm2Change(event.target.value)}
                  SelectProps={{
                    multiple: true,
                    style: {
                      padding: "12px 0px",
                    },
                    renderValue: (selected) => (
                      <MDBox display="flex" flexWrap="wrap" gap={0.5}>
                        {selected.map((reference) => {
                          const boundary = adm2Boundaries.find(
                            (item) => item.reference === reference
                          );

                          return (
                            <Chip
                              key={reference}
                              size="small"
                              label={boundary ? boundary.title : reference}
                            />
                          );
                        })}
                      </MDBox>
                    ),
                  }}
                >
                  {adm2Boundaries.map((boundary) => (
                    <MenuItem key={boundary.reference} value={boundary.reference}>
                      <Checkbox checked={selectedAdm2.includes(boundary.reference)} />

                      <MDTypography variant="body2">{boundary.title}</MDTypography>
                    </MenuItem>
                  ))}
                </MDInput>
              )}

              {(selectedAdm0 || selectedAdm1.length > 0 || selectedAdm2.length > 0) && (
                <MDBox mt={2}>
                  <MDTypography variant="caption" color="text">
                    Selected boundaries:{" "}
                    {selectedAdm2.length > 0
                      ? `${selectedAdm2.length} LGA / District${
                          selectedAdm2.length === 1 ? "" : "s"
                        }`
                      : selectedAdm1.length > 0
                      ? `${selectedAdm1.length} State / Province${
                          selectedAdm1.length === 1 ? "" : "s"
                        }`
                      : "1 Country"}
                  </MDTypography>
                </MDBox>
              )}
            </MDBox>
          )}
        </DialogContent>

        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenAddDialog(false);
              resetBoundarySelection();
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>

          <MDButton
            color="dark"
            variant="contained"
            onClick={() => {
              addBoundaries();
            }}
          >
            Add
          </MDButton>
        </DialogActions>
      </Dialog>

      <MDBox pt={1} pb={3}>
        {pageLoaded && (
          <MDBox>
            <MDBox mb={1}>
              <MDButton
                variant="text"
                color="dark"
                onClick={() => navigate("/geo/territories")}
                startIcon={<Icon>arrow_back</Icon>}
              >
                Territories
              </MDButton>
            </MDBox>

            <Card
              sx={{
                height: "100%",
                marginTop: "0px",
              }}
            >
              <MDBox p={2}>
                <MDBox display="flex" justifyContent="space-between" alignItems="center">
                  <MDBox>
                    <MDTypography variant="h6" fontWeight="medium">
                      {territory.title}
                    </MDTypography>

                    <MDTypography variant="button" color="text" fontWeight="light">
                      Manage the boundaries that make up this territory.
                    </MDTypography>
                  </MDBox>

                  <MDButton
                    variant="contained"
                    color="dark"
                    onClick={() => {
                      resetBoundarySelection();
                      setBoundaryType("custom");
                      setOpenAddDialog(true);
                    }}
                    startIcon={<Icon>add</Icon>}
                  >
                    Add Boundary
                  </MDButton>
                </MDBox>
              </MDBox>

              <Divider />

              {territoryBoundaries.length === 0 ? (
                <MDBox p={5} textAlign="center">
                  <Icon
                    sx={{
                      fontSize: 48,
                      color: "text.secondary",
                    }}
                  >
                    map
                  </Icon>

                  <MDTypography variant="h6" mt={1}>
                    No boundaries assigned
                  </MDTypography>

                  <MDTypography variant="button" color="text">
                    Add boundaries to define this territory.
                  </MDTypography>
                </MDBox>
              ) : (
                <Stack p={2} gap={2}>
                  {territoryBoundaries.map((boundary) => (
                    <ListItem
                      key={boundary.reference}
                      secondaryAction={
                        <Tooltip title="Remove Boundary">
                          <MDButton
                            size="small"
                            variant="outlined"
                            color="error"
                            iconOnly
                            onClick={() => removeBoundary(boundary.reference)}
                          >
                            <Icon fontSize="small">delete</Icon>
                          </MDButton>
                        </Tooltip>
                      }
                    >
                      <ListItemText
                        primary={
                          <MDTypography variant="body2" fontWeight="regular">
                            {boundary.boundary}
                          </MDTypography>
                        }
                      />
                    </ListItem>
                  ))}
                </Stack>
              )}
            </Card>
          </MDBox>
        )}
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

export default TerritoryPage;
